import type { Filter } from '$lib/components/filters/store.svelte';

import { error } from '@sveltejs/kit';
import { brandsSlugs, truncate } from '$lib';
import { m } from '$lib/paraglide/messages';

import type { PageLoadEvent } from './$types';

import CategoryPage from './CategoryPage.svelte';

export async function fetchContent(
	storeId: number,
	urlParams: URLSearchParams,
	fetch = window.fetch,
	includeFilters = false,
) {
	urlParams.set('include_filters', includeFilters.toString());
	const products_response = await fetch(`/api/content/category?${urlParams}`, {
		headers: {
			'X-Store-ID': storeId.toString(),
		},
	});
	if (products_response.status === 404) error(404);
	if (products_response.status !== 200) error(500);

	const content = (await products_response.json()) as {
		category: Category | null;
		products: ProductFromCategory[];
		filters: Filter[] | null;
	};

	return {
		count: products_response.headers.get('x-count') ?? '0',
		category: content?.category ?? null,
		products: content?.products ?? [],
		filters: content?.filters ?? [],
	};
}

export type CategoryPageData = {
	slug: string;
	count: string | null;
	pageSize: number;
	category: Category | null;
	products: ProductFromCategory[];
	filters: Filter[];
	isBrand: boolean;
};
export const categoryLoad = async (
	{ parent, params, fetch, url }: PageLoadEvent,
	isBrand = false,
) => {
	const { store, title } = await parent();

	const brandSlug = brandsSlugs[store.language];
	let slug = params.slug;

	if (isBrand) {
		slug = slug.replace(new RegExp(`^${brandSlug}/`), '');
	}
	const pageSize = 24;

	const urlParams = new URLSearchParams({
		...Object.fromEntries(url.searchParams),
		slug,
		from: '0',
		size: pageSize.toString(),
		is_brand: isBrand.toString(),
	});
	const pageData = {
		slug,
		pageSize,
		isBrand,
		...(await fetchContent(store.store_id, urlParams, fetch, true)),
	} satisfies CategoryPageData;

	if (isBrand && pageData.category) {
		pageData.category.breadcrumbs = [
			{ name: m.brands(), slug: brandsSlugs[store.language] },
			{ name: pageData.category.name, slug: `${brandSlug}/${slug}` },
		];
	}

	let metaDescription: string | undefined = undefined;
	if (pageData.category?.meta_description) {
		metaDescription = pageData.category.meta_description;
	} else if (pageData.category?.header_description) {
		metaDescription = truncate(pageData.category.header_description, 255);
	} else if (pageData.category?.footer_description) {
		metaDescription = truncate(pageData.category.footer_description, 255);
	} else if (pageData.category?.h1_title || pageData.category?.name) {
		metaDescription = m.default_category_metadescription({
			categoryName: pageData.category.h1_title ?? pageData.category.name,
			storeName: store.name,
		});
	}

	return {
		component: CategoryPage,
		pageData: pageData,
		title: pageData.category?.h1_title || pageData.category?.name || title,
		metaDescription,
	};
};
